import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

const LoginForm = ({ onSwitchToMFA, onSwitchToRecovery, onSwitchToSignup }) => {
  const navigate = useNavigate();
  const { signIn, signInWithGoogle } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    role: 'retail',
    rememberDevice: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const roleOptions = [
    { value: 'retail', label: 'Retail User' },
    { value: 'enterprise', label: 'Enterprise Client' },
    { value: 'partner', label: 'Retail Partner' },
    { value: 'admin', label: 'Administrator' }
  ];

  const dashboardRoutes = {
    retail: '/retail-user-dashboard',
    enterprise: '/enterprise-client-dashboard',
    partner: '/retail-partner-dashboard',
    admin: '/super-administrator-dashboard',
    free: '/retail-user-dashboard',
    premium: '/retail-user-dashboard'
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleRoleChange = (value) => {
    setFormData(prev => ({ ...prev, role: value }));
  };

  const handleCheckboxChange = (e) => {
    setFormData(prev => ({ ...prev, rememberDevice: e?.target?.checked }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.email) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const getUserRole = async (userId) => {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('role')
        ?.eq('id', userId)
        ?.single();
      
      if (error) throw error;
      return data?.role || formData?.role;
    } catch (error) {
      console.error('Error fetching user role:', error);
      return formData?.role;
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);

    try {
      const { data, error } = await signIn(formData?.email, formData?.password);
      
      if (error) {
        setErrors({
          email: error?.message || 'Invalid email or password',
          password: error?.message || 'Invalid email or password'
        });
      } else if (data?.user) {
        // Fetch user's actual role from database
        const userRole = await getUserRole(data?.user?.id);
        
        // Navigate to appropriate dashboard based on role
        const route = dashboardRoutes?.[userRole] || dashboardRoutes?.[formData?.role] || '/retail-user-dashboard';
        navigate(route);
      }
    } catch (error) {
      setErrors({
        email: 'An error occurred. Please try again.',
        password: 'An error occurred. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      // Store selected role in localStorage for post-OAuth routing
      localStorage.setItem('pendingUserRole', formData?.role);
      
      const { error } = await signInWithGoogle();
      if (error) {
        setErrors({
          email: error?.message || 'Google sign-in failed. Please try again.'
        });
        localStorage.removeItem('pendingUserRole');
      }
    } catch (error) {
      setErrors({
        email: 'An error occurred. Please try again.'
      });
      localStorage.removeItem('pendingUserRole');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">Welcome Back</h2>
        <p className="text-sm text-muted-foreground">Sign in to access your account</p>
      </div>

      <Input
        label="Email Address"
        type="email"
        name="email"
        placeholder="Enter your email"
        value={formData?.email}
        onChange={handleInputChange}
        error={errors?.email}
        required
        disabled={loading}
      />
      <Input
        label="Password"
        type="password"
        name="password"
        placeholder="Enter your password"
        value={formData?.password}
        onChange={handleInputChange}
        error={errors?.password}
        required
        disabled={loading}
      />
      
      <Select
        label="Login As"
        value={formData?.role}
        onChange={handleRoleChange}
        options={roleOptions}
        disabled={loading}
      />
      
      <div className="flex items-center justify-between">
        <Checkbox
          label="Remember this device"
          checked={formData?.rememberDevice}
          onChange={handleCheckboxChange}
          disabled={loading}
        />
        <button
          type="button"
          onClick={onSwitchToRecovery}
          className="text-sm font-medium text-fintech-blue-600 hover:text-fintech-purple-600 transition-smooth"
          disabled={loading}
        >
          Forgot password?
        </button>
      </div>
      <Button
        type="submit"
        variant="default"
        fullWidth
        loading={loading}
        iconName="LogIn"
        iconPosition="right"
        className="bg-gradient-to-r from-fintech-blue-500 to-fintech-purple-500 hover:from-fintech-blue-600 hover:to-fintech-purple-600 shadow-glow-blue"
      >
        Sign In
      </Button>
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-fintech-blue-200"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-white text-muted-foreground">Or continue with</span>
        </div>
      </div>
      <Button
        type="button"
        variant="outline"
        fullWidth
        onClick={handleGoogleLogin}
        disabled={loading}
        iconName="Mail"
        iconPosition="left"
        className="border-fintech-blue-300 hover:bg-fintech-blue-50 hover:border-fintech-blue-500 transition-smooth"
      >
        Continue with Google
      </Button>

      <div className="text-center">
        <p className="text-sm text-muted-foreground">
          Don't have an account?{' '}
          <button
            type="button"
            onClick={onSwitchToSignup}
            className="text-fintech-blue-600 hover:text-fintech-purple-600 transition-smooth font-medium"
            disabled={loading}
          >
            Sign up
          </button>
        </p>
      </div>
    </form>
  );
};

export default LoginForm;